/**
 * [MEMEET-VFX] 2026-11-05 — Arka plan seçici: kapalı, hafif/güçlü bulanıklık, hazır görseller, kendi görseli.
 * Girişten önceki ekranda (ön izleme) ve toplantı içi cihaz ayarlarında kullanılır.
 * standalone / memeet-core-ui / socialfolders/MeMeet kopyalarında BİREBİR aynıdır.
 */
import React, { useEffect, useRef, useState } from "react";
import "./VideoEffectsPicker.css";
import {
  BUILTIN_BACKGROUNDS,
  CUSTOM_IMAGE_ID,
  VideoEffect,
  backgroundImageUrl,
  readCustomImage,
  saveCustomImage,
  videoEffectsSupported,
} from "./videoEffects";

type T = (key: string, fallback: string) => string;

interface Props {
  value: VideoEffect;
  onChange: (v: VideoEffect) => void;
  t: T;
  /** Uygulama sırasında (model yüklenirken) gösterilecek ince durum. */
  busy?: boolean;
  compact?: boolean;
}

function NoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M6 18 18 6" />
    </svg>
  );
}
function BlurIcon({ strong }: { strong?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="9" r="3.2" />
      <path d="M6 19.5c.6-3.1 3.1-5 6-5s5.4 1.9 6 5" opacity="0.95" />
      {[3, 21].map((x) => [5, 12, 19].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r={strong ? 1.5 : 1} opacity={strong ? 0.9 : 0.55} />))}
      {strong ? [7.5, 16.5].map((x) => <circle key={`m${x}`} cx={x} cy="3.5" r="1.2" opacity="0.8" />) : null}
    </svg>
  );
}
function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 16V5" />
      <path d="m7.5 9.5 4.5-4.5 4.5 4.5" />
      <path d="M5 15v3.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V15" />
    </svg>
  );
}

const same = (a: VideoEffect, b: VideoEffect) => JSON.stringify(a) === JSON.stringify(b);

export default function VideoEffectsPicker({ value, onChange, t, busy, compact }: Props) {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [custom, setCustom] = useState<string | null>(() => readCustomImage());
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    void videoEffectsSupported().then((ok) => { if (alive) setSupported(ok); });
    return () => { alive = false; };
  }, []);

  const pick = (v: VideoEffect) => { if (!same(v, value)) onChange(v); };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\//.test(f.type)) { setUploadErr(t("memeetBgUploadInvalid", "Lütfen bir görsel dosyası seçin.")); return; }
    setUploadErr(null);
    try {
      const url = await saveCustomImage(f);
      setCustom(url);
      onChange({ mode: "image", imageId: CUSTOM_IMAGE_ID });
    } catch {
      setUploadErr(t("memeetBgUploadFailed", "Görsel yüklenemedi."));
    }
  };

  if (supported === false) {
    return (
      <div className={`memeet-vfx ${compact ? "is-compact" : ""}`}>
        <div className="memeet-vfx-title">{t("memeetBackground", "Arka plan")}</div>
        <p className="memeet-vfx-note">{t("memeetBgUnsupported", "Bu tarayıcı arka plan efektlerini desteklemiyor.")}</p>
      </div>
    );
  }

  return (
    <div className={`memeet-vfx ${compact ? "is-compact" : ""}`} aria-busy={busy ? true : undefined}>
      <div className="memeet-vfx-title">
        {t("memeetBackground", "Arka plan")}
        {busy ? <span className="memeet-vfx-busy" aria-hidden="true" /> : null}
      </div>
      <div className="memeet-vfx-row" role="radiogroup" aria-label={t("memeetBackground", "Arka plan")}>
        <button type="button" role="radio" aria-checked={value.mode === "none"} className={`memeet-vfx-opt ${value.mode === "none" ? "is-active" : ""}`}
          onClick={() => pick({ mode: "none" })} title={t("memeetBgNone", "Kapalı")} disabled={supported === null}>
          <NoneIcon /><span>{t("memeetBgNone", "Kapalı")}</span>
        </button>
        <button type="button" role="radio" aria-checked={value.mode === "blur" && value.level === "light"}
          className={`memeet-vfx-opt ${value.mode === "blur" && value.level === "light" ? "is-active" : ""}`}
          onClick={() => pick({ mode: "blur", level: "light" })} title={t("memeetBgBlurLight", "Hafif bulanık")} disabled={supported === null}>
          <BlurIcon /><span>{t("memeetBgBlurLight", "Hafif bulanık")}</span>
        </button>
        <button type="button" role="radio" aria-checked={value.mode === "blur" && value.level === "strong"}
          className={`memeet-vfx-opt ${value.mode === "blur" && value.level === "strong" ? "is-active" : ""}`}
          onClick={() => pick({ mode: "blur", level: "strong" })} title={t("memeetBgBlurStrong", "Güçlü bulanık")} disabled={supported === null}>
          <BlurIcon strong /><span>{t("memeetBgBlurStrong", "Güçlü bulanık")}</span>
        </button>
      </div>
      <div className="memeet-vfx-grid">
        {BUILTIN_BACKGROUNDS.map((b) => {
          const active = value.mode === "image" && value.imageId === b.id;
          const label = t(b.labelKey, b.label);
          return (
            <button key={b.id} type="button" role="radio" aria-checked={active} title={label} aria-label={label}
              className={`memeet-vfx-thumb ${active ? "is-active" : ""}`} disabled={supported === null}
              onClick={() => pick({ mode: "image", imageId: b.id })}
              style={{ backgroundImage: `url("${backgroundImageUrl(b.id) || ""}")` }} />
          );
        })}
        {custom ? (
          <button type="button" role="radio" aria-checked={value.mode === "image" && value.imageId === CUSTOM_IMAGE_ID}
            title={t("memeetBgCustom", "Kendi görselim")} aria-label={t("memeetBgCustom", "Kendi görselim")}
            className={`memeet-vfx-thumb ${value.mode === "image" && value.imageId === CUSTOM_IMAGE_ID ? "is-active" : ""}`}
            disabled={supported === null}
            onClick={() => pick({ mode: "image", imageId: CUSTOM_IMAGE_ID })}
            style={{ backgroundImage: `url("${custom}")` }} />
        ) : null}
        <button type="button" className="memeet-vfx-thumb is-upload" onClick={() => fileRef.current?.click()}
          title={t("memeetBgUpload", "Görsel yükle")} aria-label={t("memeetBgUpload", "Görsel yükle")} disabled={supported === null}>
          <UploadIcon />
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
      </div>
      {uploadErr ? <p className="memeet-vfx-note is-error">{uploadErr}</p> : null}
      <p className="memeet-vfx-note">{t("memeetBgPrivacy", "Efekt cihazınızda işlenir; görüntünüz dışarı gönderilmez.")}</p>
    </div>
  );
}
